<template>
  <v-dialog :value="value" max-width="880" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>{{ $t("DEALS.VIEW.ORGANIZE_TRANSPORT_TITLE") }}</v-card-title>
      <v-card-text>
        <div class="text-body-2 grey--text mb-4">
          {{ $t(hintKey) }}
        </div>

        <v-row dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              :label="$t('DEALS.VIEW.ORGANIZE_TRANSPORT_SEARCH')"
              prepend-inner-icon="mdi-magnify"
              clearable
              hide-details
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-btn-toggle v-model="direction" mandatory dense class="deal-transport-direction">
              <v-btn small value="all">{{ $t("DEALS.VIEW.ORGANIZE_TRANSPORT_ALL") }}</v-btn>
              <v-btn small value="inbound">{{ $t("STOCK.DELIVERY_NOTES.INBOUND") }}</v-btn>
              <v-btn small value="outbound">{{ $t("STOCK.DELIVERY_NOTES.OUTBOUND") }}</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="3" class="d-flex align-center">
            <v-checkbox
              v-model="onlyAvailable"
              :label="$t('DEALS.VIEW.ORGANIZE_TRANSPORT_ONLY_AVAILABLE')"
              hide-details
              class="mt-0"
            />
          </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4" />

        <v-alert v-else-if="!filteredNotes.length" type="info" dense text class="mt-4 mb-0">
          {{ $t(emptyKey) }}
        </v-alert>

        <template v-else>
          <div class="d-flex align-center mt-3 mb-1">
            <v-checkbox
              :input-value="allVisibleSelected"
              :indeterminate="someVisibleSelected"
              hide-details
              class="mt-0 pt-0"
              :label="$t('DEALS.VIEW.ORGANIZE_TRANSPORT_SELECT_ALL')"
              @change="toggleVisible"
            />
            <v-spacer />
            <span class="text-caption grey--text">{{ selectedIds.length }}</span>
          </div>

          <v-alert v-if="selectionIsMixed" type="info" dense text class="mb-2">
            {{ $t("DEALS.VIEW.ORGANIZE_TRANSPORT_MIXED") }}
          </v-alert>

          <div
            v-for="note in filteredNotes"
            :key="note.id"
            class="deal-transport-note"
            :class="{ 'deal-transport-note--disabled': !note.can_plan }"
          >
            <v-checkbox
              :input-value="selectedIds.includes(note.id)"
              :disabled="!note.can_plan"
              hide-details
              class="mt-0 pt-0 mr-2"
              @change="toggleNote(note)"
            />
            <div class="flex-grow-1 min-width-0">
              <div class="d-flex flex-wrap align-center">
                <router-link
                  :to="{ name: 'stock-delivery-notes-view', params: { id: note.id } }"
                  class="font-weight-medium mr-2"
                  target="_blank"
                >
                  {{ note.code }}
                </router-link>
                <v-chip x-small outlined class="mr-2">{{ directionLabel(note.direction) }}</v-chip>
                <v-chip x-small :color="statusColor(note.status)" dark>{{ statusLabel(note) }}</v-chip>
                <span v-if="blockedLabel(note)" class="text-caption grey--text ml-2">
                  {{ blockedLabel(note) }}
                </span>
              </div>
              <div class="text-caption grey--text mt-1">
                <span v-if="note.company_name">{{ note.company_name }}</span>
                <template v-if="note.order_code">
                  <span v-if="note.company_name"> · </span>
                  <router-link v-if="orderRoute(note).name" :to="orderRoute(note)">{{ note.order_code }}</router-link>
                  <span v-else>{{ note.order_code }}</span>
                </template>
                <span v-if="routeLabel(note)"> · {{ routeLabel(note) }}</span>
                <span v-if="plannedDate(note)"> · {{ plannedDate(note) }}</span>
                <span> · {{ $t("DEALS.VIEW.ORGANIZE_TRANSPORT_LINES", { count: note.line_count }) }}</span>
              </div>
            </div>
          </div>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">{{ $t("GENERAL.CANCEL") }}</v-btn>
        <v-btn
          color="primary"
          :disabled="!selectedIds.length"
          :loading="creating"
          @click="createTransport"
        >
          {{ $t("DEALS.VIEW.ORGANIZE_TRANSPORT_CREATE") }}
          <span v-if="selectedIds.length"> ({{ selectedIds.length }})</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { GET_DEAL_TRANSPORT_CANDIDATES } from "@/store/deals.module";
import { CREATE_TRANSPORT_ORDER_FROM_DELIVERY_NOTES } from "@/store/stock/stockDeliveryNotes.module";
import { GET_STOCK_PO_TRANSPORT_CANDIDATES } from "@/store/stock/stockPurchaseOrders.module";
import { GET_STOCK_SO_TRANSPORT_CANDIDATES } from "@/store/stock/stockSalesOrders.module";
import { nodeRoute } from "@/views/pages/deals/dealWorkflowMeta";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "DealOrganizeTransportDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    dealId: {
      type: Number,
      default: null,
    },
    orderType: {
      type: String,
      default: null,
    },
    orderId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      creating: false,
      notes: [],
      selectedIds: [],
      search: "",
      direction: "all",
      onlyAvailable: true,
    };
  },
  computed: {
    filteredNotes() {
      const term = (this.search || "").trim().toLowerCase();
      return this.notes.filter((note) => {
        if (this.onlyAvailable && !note.can_plan) {
          return false;
        }
        if (this.direction && this.direction !== "all" && note.direction !== this.direction) {
          return false;
        }
        if (!term) {
          return true;
        }
        const haystack = [note.code, note.company_name, note.order_code, note.loading, note.unloading]
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    visibleSelectableIds() {
      return this.filteredNotes.filter((note) => note.can_plan).map((note) => note.id);
    },
    allVisibleSelected() {
      return this.visibleSelectableIds.length > 0
        && this.visibleSelectableIds.every((id) => this.selectedIds.includes(id));
    },
    someVisibleSelected() {
      const selected = this.visibleSelectableIds.filter((id) => this.selectedIds.includes(id));
      return selected.length > 0 && selected.length < this.visibleSelectableIds.length;
    },
    selectionIsMixed() {
      const selected = this.notes.filter((note) => this.selectedIds.includes(note.id));
      const directions = new Set(selected.map((note) => note.direction));
      return directions.size > 1;
    },
    hintKey() {
      return this.orderId
        ? "DEALS.VIEW.ORGANIZE_TRANSPORT_HINT_ORDER"
        : "DEALS.VIEW.ORGANIZE_TRANSPORT_HINT";
    },
    emptyKey() {
      return this.orderId
        ? "DEALS.VIEW.ORGANIZE_TRANSPORT_EMPTY_ORDER"
        : "DEALS.VIEW.ORGANIZE_TRANSPORT_EMPTY";
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.search = "";
        this.direction = this.defaultDirection();
        this.onlyAvailable = true;
        this.selectedIds = [];
        this.loadNotes();
      }
    },
  },
  methods: {
    async loadNotes() {
      this.loading = true;
      try {
        this.notes = await this.loadCandidates();
      } catch (errors) {
        this.notes = [];
        this.notify(errors);
      } finally {
        this.loading = false;
      }
    },
    defaultDirection() {
      if (this.orderType === "purchase_order") {
        return "inbound";
      }
      if (this.orderType === "sales_order") {
        return "outbound";
      }
      return "all";
    },
    loadCandidates() {
      if (this.orderType === "purchase_order" && this.orderId) {
        return this.$store.dispatch(GET_STOCK_PO_TRANSPORT_CANDIDATES, this.orderId);
      }
      if (this.orderType === "sales_order" && this.orderId) {
        return this.$store.dispatch(GET_STOCK_SO_TRANSPORT_CANDIDATES, this.orderId);
      }
      if (this.dealId) {
        return this.$store.dispatch(GET_DEAL_TRANSPORT_CANDIDATES, this.dealId);
      }
      return [];
    },
    toggleNote(note) {
      if (!note.can_plan) {
        return;
      }
      if (this.selectedIds.includes(note.id)) {
        this.selectedIds = this.selectedIds.filter((id) => id !== note.id);
        return;
      }
      this.selectedIds = [...this.selectedIds, note.id];
    },
    toggleVisible(selected) {
      if (selected) {
        this.selectedIds = Array.from(new Set([...this.selectedIds, ...this.visibleSelectableIds]));
        return;
      }
      const hidden = new Set(this.visibleSelectableIds);
      this.selectedIds = this.selectedIds.filter((id) => !hidden.has(id));
    },
    directionLabel(direction) {
      return direction === "inbound"
        ? this.$t("STOCK.DELIVERY_NOTES.INBOUND")
        : this.$t("STOCK.DELIVERY_NOTES.OUTBOUND");
    },
    statusLabel(note) {
      const key = `STOCK.DELIVERY_NOTES.STATUS_${(note.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : note.status_label;
    },
    statusColor(status) {
      return { draft: "grey", confirmed: "primary", executed: "success", invoiced: "teal", cancelled: "error" }[status] || "grey";
    },
    blockedLabel(note) {
      if (note.can_plan) {
        return "";
      }
      if (note.transport_order?.code) {
        return this.$t("DEALS.VIEW.ORGANIZE_TRANSPORT_ON_TO", { code: note.transport_order.code });
      }
      return this.$t("DEALS.VIEW.ORGANIZE_TRANSPORT_NOT_OPEN", { status: this.statusLabel(note) });
    },
    routeLabel(note) {
      if (note.loading && note.unloading) {
        return `${note.loading} → ${note.unloading}`;
      }
      return note.loading || note.unloading || "";
    },
    plannedDate(note) {
      const date = note.direction === "inbound" ? note.eta_date : note.etd_date;
      if (!date) {
        return "";
      }
      const label = note.direction === "inbound"
        ? this.$t("STOCK.DELIVERY_NOTES.ETA")
        : this.$t("STOCK.DELIVERY_NOTES.ETD");
      return `${label} ${date}`;
    },
    orderRoute(note) {
      if (!note.order_kind || !note.order_id) {
        return {};
      }
      return nodeRoute({ kind: note.order_kind, id: note.order_id });
    },
    async createTransport() {
      if (!this.selectedIds.length) {
        return;
      }
      this.creating = true;
      try {
        const payload = { delivery_note_ids: this.selectedIds };
        if (this.dealId) {
          payload.deal_id = this.dealId;
        }
        const order = await this.$store.dispatch(CREATE_TRANSPORT_ORDER_FROM_DELIVERY_NOTES, payload);
        this.$emit("input", false);
        if (order?.id) {
          this.$router.push({ name: "stock-transport-orders-view", params: { id: order.id } });
        }
      } catch (errors) {
        this.notify(errors);
      } finally {
        this.creating = false;
      }
    },
    notify(errors) {
      const fieldError = errors?.errors?.delivery_note_ids?.[0];
      this.$store.dispatch(SHOW_SNACK, {
        message: fieldError || errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.deal-transport-direction {
  height: 40px;
}

.deal-transport-note {
  display: flex;
  align-items: flex-start;
  padding: 8px 4px;
  border-top: 1px solid #eef0f5;
}

.deal-transport-note--disabled {
  opacity: 0.72;
}
</style>
